import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { IosCard, iosColors } from "mobile/components/IosParity";

const DISMISS_KEY = "vb-mobile-install-prompt-dismissed";

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator?.standalone === true);

export default function MobileInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(() =>
    typeof window !== "undefined" ? window.localStorage?.getItem(DISMISS_KEY) === "1" : true
  );

  useEffect(() => {
    if (dismissed || isStandalone() || typeof window === "undefined") return undefined;

    const ua = window.navigator?.userAgent || "";
    const isiPadDesktopMode = /macintosh/i.test(ua) && window.navigator?.maxTouchPoints > 1;
    const isiOS = /iphone|ipad|ipod/i.test(ua) || isiPadDesktopMode;
    setShowIosHint(isiOS);

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
      setShowIosHint(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  }, [dismissed]);

  if (dismissed || isStandalone() || (!installPrompt && !showIosHint)) return null;

  const dismiss = () => {
    setDismissed(true);
    window.localStorage?.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice.catch(() => null);
    setInstallPrompt(null);
    dismiss();
  };

  return (
    <IosCard sx={{ p: 1.4, borderColor: "rgba(181,71,8,0.28)", bgcolor: "rgba(255,106,0,0.06)" }}>
      <Stack spacing={1}>
        <Typography sx={{ color: iosColors.ink, fontSize: 14.5, fontWeight: 900, lineHeight: 1.2 }}>
          Add VBS to your home screen
        </Typography>
        <Typography sx={{ color: iosColors.muted, fontSize: 12.75, lineHeight: 1.35 }}>
          Install the mobile app for quick access. Workflows still require an internet connection.
        </Typography>
        {showIosHint ? (
          <Typography sx={{ color: iosColors.orange, fontSize: 12.5, fontWeight: 850, lineHeight: 1.35 }}>
            In Safari, use Share, then Add to Home Screen.
          </Typography>
        ) : null}
        <Stack direction="row" spacing={0.75}>
          {installPrompt ? (
            <Button size="small" variant="contained" onClick={install} sx={{ bgcolor: iosColors.orange }}>
              Install
            </Button>
          ) : null}
          <Button size="small" variant="text" onClick={dismiss} sx={{ color: iosColors.muted }}>
            Not now
          </Button>
        </Stack>
      </Stack>
    </IosCard>
  );
}
